import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { RoomProps } from "../../types";
import { webRoutes } from "../../../../router/webRoutes";
import ChatBubble from "../ChatBubble/ChatBubble";
import YetiCharacter from "../YetiCharacter/YetiCharacter";
import Snowfall from "../Snowfall/Snowfall";
import "./HomeRoom.scss";
import clickAnimationJson from "../../../../appAssets/jsons/ClickAnimation.json";
// Lottie animations — same set the mobile OnboardingHomeContainer uses
import plannerIconJson from "../../../../appAssets/jsons/plannerIcon.json";
import bouncyFrameJson from "../../../../appAssets/jsons/bouncyFrame.json";
import floatingPlaneJson from "../../../../appAssets/jsons/floatingPlane.json";
import videoObjectJson from "../../../../appAssets/jsons/videoObject.json";
import chatObjectJson from "../../../../appAssets/jsons/chatObject.json";
import { plannerTable } from "../../../../appAssets/jsons/index";
import {
  ExtractNameFromUrl,
  getMatchingObjectNames,
} from "../../../../Utilities/utilFunctions";
import { useCallback, useEffect, useRef, useState } from "react";

function HomeRoom({
  storyData,
  activeBlock,
  conversationIndex,
  objectIndex,
  onNavigateToJourney,
  onTabChange,
  onInputSubmit,
  onTemplateSubmit,
  onButtonAction,
  onTapIndicator,
  onShowOnboardingNavBar,
}: RoomProps) {
  const navigate = useNavigate();
  const isOnboardingDone = storyData?.isOnboardingCompleted ?? false;

  const [showButtonType, setShowButtonType] = useState({
    state: false,
    textValue: "",
  });
  const [displayHomeObject, setDisplayHomeObject] = useState({
    url: "",
    name: "",
    tapindicator: "",
  });
  const [tapIndicatorName, setTapIndicatorName] = useState<string | null>(null);
  const lastHomeObjectUrlRef = useRef("");

  const returnObjectRendering = useCallback(
    (arg0: { name: string; url: string; tapIndicator: string }) => {
      setTapIndicatorName(arg0.tapIndicator);
      onTapIndicator?.(arg0.tapIndicator);
    },
    [onTapIndicator],
  );

  useEffect(() => {
    if (!storyData?.blocks?.[0]?.conversations) return;
    const conversations = storyData.blocks[0].conversations;
    const currentConversation = conversations[conversationIndex]?.object || [];
    const isLastConversation = conversationIndex === conversations.length - 1;
    const isLastObject = objectIndex === currentConversation.length - 1;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        .tapIndicator;

    if (tapIndicator) {
      returnObjectRendering({
        name: tapIndicator,
        url: "",
        tapIndicator: tapIndicator,
      });
    }

    if (isLastConversation && isLastObject) {
      storyData?.blocks?.[0]?.userActions?.forEach(
        (action: { component: string; buttonText: string }) => {
          if (
            action?.component?.toLowerCase() === "block.button-input" &&
            !storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[
              objectIndex
            ]?.templateElements
          ) {
            setShowButtonType({ state: true, textValue: action.buttonText });
          }
        },
      );
    }
  }, [conversationIndex, objectIndex, storyData, returnObjectRendering]);

  const currentObject =
    activeBlock?.conversations?.[conversationIndex]?.object?.[objectIndex] ??
    null;

  useEffect(() => {
    const blockObjects =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.blockObjects;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.tapIndicator;
    const normalizedBlockObjects = (blockObjects ?? []).map((obj) => ({
      ...obj,
      linkTo: (obj as { linkTo?: string }).linkTo ?? "",
    }));
    const homeObject = ExtractNameFromUrl(normalizedBlockObjects);
    const homeObjectArray = Array.isArray(homeObject)
      ? homeObject
      : [homeObject];

    if (homeObjectArray.length > 0) {
      const homeExist = getMatchingObjectNames(
        [homeObjectArray?.[0]?.name],
        normalizedBlockObjects,
      );

      const incomingUrl = homeExist?.[0]?.url ?? "";
      if (homeExist && lastHomeObjectUrlRef.current !== incomingUrl) {
        lastHomeObjectUrlRef.current = incomingUrl;
        if (homeExist[0]?.name === "Nav") {
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        } else {
          setDisplayHomeObject({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            tapindicator: tapIndicator ?? "",
          });
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        }
      }
    }
  }, [conversationIndex, storyData, onShowOnboardingNavBar]);

  const userActions = activeBlock?.userActions ?? [];
  const conversationsExhausted =
    (activeBlock?.conversations?.length ?? 0) > 0 &&
    conversationIndex >= (activeBlock?.conversations?.length ?? 0);

  const handleObjectClick = (objectName: string, route: string) => {
    console.log(`object_clicked: ${objectName} from: home`);
    navigate(route);
  };

  return (
    <div className="home-room">
      {/* ── Layer 1: outdoor mountain scene ── */}
      <div
        className="home-room__window-bg"
        style={{
          backgroundImage: `url(${require("../../../../appAssets/outdoorRoom.png")})`,
        }}
      />

      {/* ── Layer 2: snowfall — visible through window cutouts ── */}
      <div className="home-room__snow-wrap">
        <Snowfall duration={6000} maxSize={10} />
      </div>

      {/* ── Layer 3: home room interior PNG ── */}
      <img
        src={require("../../../../appAssets/livingroom-web.png")}
        alt=""
        className="home-room__room-img"
      />

      {/* ── Content layers (above room PNG) ── */}

      {/* Yeti */}
      <div className="home-room__yeti">
        <YetiCharacter
          characterImage={currentObject?.characterImage}
          className="home-room__yeti-lottie"
        />
      </div>

      {/* Chat bubble */}
      {currentObject?.content?.trim() && (
        <div className="home-room__bubble">
          <ChatBubble
            content={currentObject.content}
            textColor={currentObject.textColor}
            tailSide="right"
            audioUri={currentObject.audio}
          />
        </div>
      )}

      {/* ── Interactive objects (only post-onboarding) ── */}
      {isOnboardingDone && (
        <button
          className="home-room__obj home-room__obj--balloon"
          onClick={() => {
            onNavigateToJourney?.();
          }}
          aria-label="My Journey"
        >
          <Lottie
            animationData={floatingPlaneJson}
            loop
            autoplay
            className="home-room__obj-lottie"
          />
        </button>
      )}

      {isOnboardingDone && (
        <button
          className="home-room__obj home-room__obj--video"
          onClick={() =>
            handleObjectClick("magic_backpack", webRoutes.magicBackpack)
          }
          aria-label="Videos"
        >
          <Lottie
            animationData={videoObjectJson}
            loop
            autoplay
            className="home-room__obj-lottie"
          />
        </button>
      )}

      {isOnboardingDone && (
        <button
          className="home-room__obj home-room__obj--frame"
          onClick={() => {}}
          aria-label="My Wall"
        >
          <span className="home-room__obj-tooltip">
            This feature is only available in the app for now
          </span>
          <Lottie
            animationData={bouncyFrameJson}
            loop
            autoplay
            className="home-room__obj-lottie"
          />
        </button>
      )}

      {isOnboardingDone && (
        <button
          className="home-room__obj home-room__obj--chat"
          onClick={() =>
            handleObjectClick("chat_with_troo", webRoutes.chatWithTroo)
          }
          aria-label="Chat"
        >
          <Lottie
            animationData={chatObjectJson}
            loop
            autoplay
            className="home-room__obj-lottie"
          />
        </button>
      )}

      {(isOnboardingDone ||
        displayHomeObject?.name?.toLowerCase() === "giftbox" ||
        displayHomeObject?.name?.toLowerCase() === "gift") && (
        <button
          className="home-room__obj home-room__obj--planner"
          onClick={() => {
            if (
              !isOnboardingDone &&
              (displayHomeObject?.name?.toLowerCase() === "giftbox" ||
                displayHomeObject?.name?.toLowerCase() === "gift")
            ) {
              onShowOnboardingNavBar?.({
                url: "",
                name: "",
                indicatorType: "",
              });
              onButtonAction?.();
            }
          }}
          aria-label="Travel Planner"
        >
          {isOnboardingDone && (
            <span className="home-room__obj-tooltip">
              This feature is only available in the app for now
            </span>
          )}
          <div className="home-room__planner-stack">
            <Lottie
              animationData={plannerIconJson}
              loop
              autoplay
              className="home-room__planner-icon"
            />
          </div>
          <Lottie
            animationData={plannerTable}
            loop
            autoplay
            className="home-room__planner-table"
          />
        </button>
      )}

      {/* User action buttons */}
      {conversationsExhausted && userActions.length > 0 && (
        <div className="home-room__actions">
          {userActions.map((action, i) => (
            <button key={i} className="home-room__action-btn">
              {action.buttonText}
            </button>
          ))}
        </div>
      )}

      {/* Button-input action — bottom-center, onboarding only */}
      {!isOnboardingDone && showButtonType.state && (
        <div className="home-room__bottom-action">
          <button
            type="button"
            className="home-room__bottom-btn"
            onClick={() => {
              setShowButtonType({ state: false, textValue: "" });
              onShowOnboardingNavBar?.({
                url: "",
                name: "",
                indicatorType: "",
              });
              onButtonAction?.();
            }}
          >
            {showButtonType.textValue || "Continue"}
          </button>
        </div>
      )}
    </div>
  );
}

export default HomeRoom;
